import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Formik, Form } from 'formik';
import { Grid, FormGroup, FormControlLabel, FormHelperText, Checkbox, TextField, Typography } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import Card from '../../../components/Card';
import Loader from '../../../components/Loader';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import Button from '../../../components/Button';
import ButtonConfirmation from '../../../components/ButtonConfirmation';

import { AddCircle } from '@material-ui/icons';
import MaterialIcon from 'material-icons-react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWindows } from '@fortawesome/free-brands-svg-icons';

import { clearBusinessInfo, clearEstablishmentErrors, setEditingState, setEstablishmentId, setPreviousEstablishmentId } from '../store/actions';
import { loadProvisionalReceiptInfo } from '../../ProvisionalReceiptConfig/store/thunk';

import {
  loadBusinessInfo,
  loadAddress,
  loadSuperlogicaList,
  loadBusinessConceptList,
  upsertEstablishment,
  changeEstablishmentPermission,
  setDebitWarning,
  setActivePremiumPackage,
  setDisablePremiumPackage,
  loadJumpCarStoreDownload
} from '../store/thunk';

import { handleMultipleSelection } from '../../../helpers/common';
import { useScreenMeasure } from '../../../helpers/hooks/useScreenMeasure';
import useWindowDimensions from '../../../helpers/hooks/useWindowDimensions';
import { RESTRICTION_PAGE } from '../../../helpers/restrictions';

import { businessTypes, businessTypesAdmin } from './resources';

import { validations, superlogicaValidations } from './validations';

export default function BusinessInfo({ formikProps }) {
  const dispatch = useDispatch();

  const [isMobile] = useScreenMeasure();
  const { width } = useWindowDimensions();
  const isSm = width < 768;

  const history = useHistory();
  const location = useLocation();
  const { path } = useRouteMatch();

  const registerPath = path.split('/').includes('novo-estabelecimento');
  const businessPath = path.split('/').includes('cadastro-inicial');
  const configurationPath = path.split('/').includes('configuracoes-de-notas-fiscais');

  const isEstablishmentEdit = location.pathname == '/estabelecimento';
  const isEstablishmentRegistration = path.split('/').includes('cadastro-inicial') && path.split('/').includes('estabelecimento');
  const isNewEstablishment = location.pathname == '/novo-estabelecimento';

  const {
    userId,
    resaleId,
    userRestrictions,
    submit: {
      isAdminUser
    }
  } = useSelector(state => state.profile);

  const {
    establishmentId,
    establishmentTypes,
    businessId,
    submit,
    errors: establishmentErrors,
    superlogicaList,
    jumpCarStoreDownload,
    isJumpCarStoreDownloadLoading,
    isJumpCarStoreDownloadAvailable,
    isLoading,
    isEditing,
    isPostalCodeLoading
  } = useSelector(state => state.businessInfo);

  const establishments = useSelector(state => state.login.establishments);

  const formattedEstablishments = establishments
    ?.filter(establishment => !!establishment?.establishmentId)
    ?.map(establishment => {
      return {
        ...establishment,
        label: `${establishment?.establishmentId} ${establishment?.establishmentName}`
      }
    });

  const establishmentCreatedByUserId = useSelector(state => state.businessInfo.submit.userId);

  const initialValues = {
    userId,
    establishmentId: registerPath ? '' : establishmentId,
    ...submit
  }

  const isUserAdmin = +isAdminUser == 1;
  const premiumPackage = +submit?.premiumPackage;
  const appPermission = +submit?.appPermission;
  const isStore = establishmentTypes?.includes(3);

  const canUserSeeEstablishmentTypeSelection = +isAdminUser == 1 || (!configurationPath && !isEstablishmentEdit);

  const [superlogicaState, setSuperlogicaState] = useState({
    superlogicaId: null,
    name: '',
    phone: '',
    email: ''
  });

  const [superlogicaErrorsState, setSuperlogicaErrorsState] = useState({
    isError: true,
    name: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    if(establishmentId && isEstablishmentEdit) {
      const params = {
        userId,
        establishmentId
      }

      dispatch(loadBusinessInfo(params));
    }
  }, [establishmentId, isEstablishmentEdit]);

  useEffect(() => {
    if(isNewEstablishment) {
      if(!resaleId) {
        dispatch(loadSuperlogicaList(userId));
      }
    }
  }, [isNewEstablishment]);

  useEffect(() => {
    if(!businessPath) {
      dispatch(loadJumpCarStoreDownload(userId));
    }
  }, []);

  useEffect(() => {
    if(
      userRestrictions?.includes(RESTRICTION_PAGE.VIEW_ESTABLISHMENT) &&
      userRestrictions?.includes(RESTRICTION_PAGE.CREATE_ESTABLISHMENT) &&
      userRestrictions?.includes(RESTRICTION_PAGE.EDIT_ESTABLISHMENT)
    ) {
      toast.error('Ação não autorizada.');
      history.push('/visao-geral');
    }

    if(registerPath) {
      dispatch(clearBusinessInfo());
      dispatch(clearEstablishmentErrors());
      dispatch(setPreviousEstablishmentId(establishmentId));
    }

    if(configurationPath) {
      dispatch(loadProvisionalReceiptInfo(userId, establishmentId));
    }
  }, [userRestrictions, registerPath, configurationPath]);

  useEffect(() => {
    const newEstablishmentPath = path.split('/').includes('novo-estabelecimento');

    if(newEstablishmentPath && !isEditing) {
      dispatch(setEditingState(true));
      dispatch(clearBusinessInfo());
      dispatch(clearEstablishmentErrors());
    }
  }, [isEditing]);

  const getOptionLabel = option => {
    const { label } = option || {};

    if(label) {
      return label;
    }

    return '';
  }

  const filterOptions = (options, state) => {
    const OPTIONS_LIMIT = 500;
    const defaultFilterOptions = createFilterOptions();

    return defaultFilterOptions(options, state).slice(0, OPTIONS_LIMIT);
  }

  const handleEstablishmentTypes = (e, formikNewProps) => {
    const { values } = formikNewProps;
    const { checked, value } = e.target;
    const type = parseInt(value, 10);
    const establishmentTypes = handleMultipleSelection(checked, type, values.establishmentTypes);

    formikNewProps.setFieldValue('establishmentTypes', establishmentTypes);
    formikProps.setFieldValue('establishmentTypes', establishmentTypes);
  }

  const handlePostalCode = (postalCode, setFieldValue) => {
    if(postalCode?.length === 9) {
      return dispatch(loadAddress(postalCode)).then(data => {
        const { logradouro, bairro, localidade, uf } = data;

        setFieldValue('street', logradouro);
        setFieldValue('district', bairro);
        setFieldValue('city', localidade);
        setFieldValue('state', uf);

        formikProps.setFieldValue('street', logradouro);
        formikProps.setFieldValue('district', bairro);
        formikProps.setFieldValue('city', localidade);
        formikProps.setFieldValue('state', uf);
      });
    }
  }

  const handleEstablishmentPermission = e => {
    const { value } = e.currentTarget;
    const appButton = value == '0' ? 1 : 0;

    const params = {
      appPermission: appButton
    }

    dispatch(changeEstablishmentPermission(userId, establishmentId, params));
  }

  const handleEstablishmentDebit = e => {
    const { value } = e.currentTarget;
    const appButton = value === '0' ? 1 : 0;
    const params = { debitWarning: appButton }

    dispatch(setDebitWarning(userId, establishmentId, params));
  }

  const handleBusinessInfoSave = values => {
    const isStoreOnly = values?.establishmentTypes?.length == 1 && +values?.establishmentTypes[0] == 3;

    values.userId = userId;

    if(isStoreOnly) {
      values.vacanciesMarks = 0;
    }

    if(isNewEstablishment) {
      if(superlogicaState.superlogicaId == 'newClientSl') {
        delete values['superlogicaId'];

        values.newClientSl = 1;
        values.slName = superlogicaState.name;
        values.slPhone = superlogicaState.phone;
        values.slEmail = superlogicaState.email;
      }

      if(Number.isInteger(+superlogicaState.superlogicaId)) {
        delete values['slName'];
        delete values['slPhone'];
        delete values['slEmail'];

        values.newClientSl = 0;
        values.superlogicaId = +superlogicaState.superlogicaId;
      }
    }

    if(!!resaleId) {
      delete values['newClientSl'];
      delete values['superlogicaId'];
      delete values['slName'];
      delete values['slPhone'];
      delete values['slEmail'];
    }

    if(+isAdminUser != 1) {
      delete values['establishmentTypes'];
    }

    return dispatch(upsertEstablishment(values, true)).then(async establishmentData => {
      const { establishmentTypes, establishmentId, isUpdated } = establishmentData;

      dispatch(clearEstablishmentErrors());

      if(isUpdated) {
        return dispatch(setEditingState(false));
      }

      await dispatch(setEstablishmentId({ establishmentId }));

      if(registerPath) {
        await history.push('/visao-geral');
        return;
      }

      if(establishmentTypes.includes(1) || establishmentTypes.includes('1')) {
        await history.push('/tabelas-de-preco');
      } else {
        await history.push('/servicos');
      }

      await dispatch(setEditingState(false));
    });
  }

  return(
    <>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10, marginBottom: 10 }}>
        {(!isUserAdmin && !businessPath) && (
          <Button
            type="button"
            color="primary"
            onClick={() => {
              if(location.pathname == '/novo-estabelecimento') {
                return window.location.reload();
              }

              return history.push('/novo-estabelecimento');
            }}
            startIcon={<AddCircle />}
          >
            Adicionar estabelecimento
          </Button>
        )}
        {isStore && (
          <Button
            type="button"
            color="#FF7D34"
            disabled={!isJumpCarStoreDownloadAvailable}
            loading={isJumpCarStoreDownloadLoading}
            onClick={() => window.open(jumpCarStoreDownload?.downloadLink, '_blank')}
            startIcon={
              <FontAwesomeIcon
                icon={faWindows}
                color="#FFFFFF"
                size="xs"
              />
            }
          >
            Jump Car Store ({jumpCarStoreDownload?.versionName})
          </Button>
        )}
      </div>
      <Card title="Preencha as informações básicas do negócio">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validations}
          onSubmit={handleBusinessInfoSave}
        >
          {({ ...formikNewProps }) => {
            const {
              setFieldValue,
              handleChange,
              errors,
              values: {
                debitWarning,
                establishmentTypes,
                establishmentName,
                companyName,
                documentId,
                phone,
                email,
                postalCode,
                street,
                number,
                complement,
                district,
                city,
                state,
                vacanciesMarks,
                businessEstablishmentId
              }
            } = formikNewProps;

            const isStoreOnly = establishmentTypes?.length == 1 && +establishmentTypes[0] == 3;

            return(
              <Grid item xs={12}>
                <Form>
                  <Loader isLoading={isLoading}>
                    {canUserSeeEstablishmentTypeSelection && (
                      <Grid style={{ marginBottom: 15 }}>
                        <Typography variant="subtitle1" color="textSecondary" style={{ marginBottom: 10 }}>
                          Selecione o(s) serviço(s) que o seu negócio oferece:
                        </Typography>
                        <FormGroup column>
                          {isUserAdmin && (
                            <Grid container spacing={2}>
                              {businessTypesAdmin.map(business => (
                                <Grid item xs={12} md={12 / businessTypesAdmin.length}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#FCFCFC', border: '1px solid #D7D7D7', padding: 10 }}>
                                    <FormControlLabel
                                      labelPlacement="end"
                                      label={
                                        <Typography style={{ color: '#1E5168' }}>
                                          {business.label}
                                        </Typography>
                                      }
                                      control={
                                        <Checkbox
                                          style={{ color: '#1E5168' }}
                                          type="checkbox"
                                          value={business.id}
                                          checked={establishmentTypes?.includes(business.id)}
                                          onChange={e => handleEstablishmentTypes(e, formikNewProps)}
                                        />
                                      }
                                    />
                                    {+business.id == 3 ? (
                                      <MaterialIcon icon={business.icon} color="#022A5C" />
                                    ) : (
                                      <img src={business.icon} width='30' height='30' />
                                    )}
                                  </div>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                          {!isUserAdmin && (
                            <Grid container spacing={2}>
                              {businessTypes.map(business => (
                                <Grid item xs={12} md={12 / businessTypes.length}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#FCFCFC', border: '1px solid #D7D7D7', padding: 10 }}>
                                    <FormControlLabel
                                      labelPlacement="end"
                                      label={
                                        <Typography style={{ color: '#1E5168' }}>
                                          {business.label}
                                        </Typography>
                                      }
                                      control={
                                        <Checkbox
                                          style={{ color: '#1E5168' }}
                                          type="checkbox"
                                          value={business.id}
                                          checked={establishmentTypes?.includes(business.id) || formikProps.values.establishmentTypes?.includes(business.id)}
                                          onChange={e => handleEstablishmentTypes(e, formikNewProps || formikProps)}
                                        />
                                      }
                                    />
                                    {+business.id == 3 ? (
                                      <MaterialIcon icon={business.icon} color="#022A5C" />
                                    ) : (
                                      <img src={business.icon} width='30' height='30' />
                                    )}
                                  </div>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </FormGroup>
                        <FormHelperText error>
                          {errors.establishmentTypes || formikProps.errors.establishmentTypes || establishmentErrors?.establishmentTypes}
                        </FormHelperText>
                      </Grid>
                    )}
                    <Grid container spacing={2}>
                      <Grid item sm={6} xs={12}>
                        <Input
                          autoFocus={!isMobile}
                          name="establishmentName"
                          label="Nome do estabelecimento"
                          value={
                            establishmentName ||
                            formikProps.values.establishmentName
                          }
                          error={
                            isEstablishmentRegistration
                              ? (formikProps.errors.establishmentName || !!establishmentErrors?.establishmentName)
                              : (errors.establishmentName || !!establishmentErrors?.establishmentName)
                          }
                          helperText={
                            isEstablishmentRegistration
                              ? (formikProps.errors.establishmentName || establishmentErrors?.establishmentName)
                              : (errors.establishmentName || establishmentErrors?.establishmentName)
                          }
                          onChange={e => {
                            handleChange(e);
                            formikProps.handleChange(e);
                          }}
                        />
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Input
                          name="documentId"
                          type="document"
                          label="CNPJ (Opcional)"
                          placeholder="00.000.000/0000-00"
                          value={documentId || formikProps.values.documentId}
                          error={errors.documentId || formikProps.errors.documentId}
                          helperText={errors.documentId || formikProps.errors.documentId}
                          onChange={e => {
                            handleChange(e);
                            formikProps.handleChange(e);
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid item sm={6} xs={12}>
                        <Input
                          noAutoComplete
                          name="phone"
                          type="phone"
                          label="Telefone"
                          placeholder="(00) 00000-0000"
                          value={phone || formikProps.values.phone}
                          error={errors.phone || formikProps.errors.phone}
                          helperText={errors.phone || formikProps.errors.phone}
                          onChange={e => {
                            handleChange(e);
                            formikProps.handleChange(e);
                          }}
                        />
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Input
                          name="email"
                          type="email"
                          label="Email"
                          placeholder="usuario@exemplo.com.br"
                          value={email || formikProps.values.email}
                          error={errors.email || formikProps.errors.email}
                          helperText={errors.email || formikProps.errors.email}
                          onChange={e => {
                            handleChange(e);
                            formikProps.handleChange(e);
                          }}
                        />
                      </Grid>
                    </Grid>
                    {configurationPath && (
                      <Grid item xs={12}>
                        <Input
                          name="companyName"
                          label="Razão social"
                          value={companyName || formikProps.values.companyName}
                          error={errors.companyName || formikProps.errors.companyName}
                          helperText={errors.companyName || formikProps.errors.companyName}
                          onChange={e => {
                            handleChange(e);
                            formikProps.handleChange(e);
                          }}
                        />
                      </Grid>
                    )}
                    <Grid container spacing={2}>
                      <Grid item lg={2} sm={6} xs={12}>
                        {(isNewEstablishment && isMobile) && (
                          <Typography variant="subtitle2" color="primary" style={{ borderBottom: '1px solid #E4E7EA', marginBottom: 10 }}>
                            Local onde o serviço é prestado
                          </Typography>
                        )}
                        <Input
                          name="postalCode"
                          type="postalCode"
                          label="CEP"
                          placeholder="00000-000"
                          value={postalCode || formikProps.values.postalCode}
                          error={errors.postalCode || formikProps.errors.postalCode}
                          onChange={e => {
                            handleChange(e);
                            formikProps.handleChange(e);
                            handlePostalCode(e.target.value, setFieldValue);
                          }}
                        />
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Typography variant="subtitle2" />
                        <Input
                          name="street"
                          type="text"
                          label="Endereço"
                          placeholder="Endereço"
                          loading={isPostalCodeLoading}
                          maxLength={255}
                          value={street || formikProps.values.street || ''}
                          error={
                            isEstablishmentRegistration
                              ? (formikProps.errors.street || !!establishmentErrors?.street)
                              : (errors.street || !!establishmentErrors?.street)
                          }
                          helperText={
                            isEstablishmentRegistration
                              ? (formikProps.errors.street || establishmentErrors?.street)
                              : (errors.street || establishmentErrors?.street)
                          }
                          onChange={e => {
                            handleChange(e);
                            formikProps.handleChange(e);
                          }}
                        />
                      </Grid>
                      <Grid item lg={2} xs={6}>
                        <Input
                          name="number"
                          type="number"
                          label="Número"
                          placeholder="0"
                          loading={isPostalCodeLoading}
                          maxLength={8}
                          value={number || formikProps.values.number || ''}
                          error={
                            isEstablishmentRegistration
                              ? (formikProps.errors.number || !!establishmentErrors?.number)
                              : (errors.number || !!establishmentErrors?.number)
                          }
                          helperText={
                            isEstablishmentRegistration
                              ? (formikProps.errors.number || establishmentErrors?.number)
                              : (errors.number || establishmentErrors?.number)
                          }
                          onChange={e => {
                            handleChange(e);
                            formikProps.handleChange(e);
                          }}
                        />
                      </Grid>
                      <Grid item lg={2} xs={6}>
                        <Input
                          name="complement"
                          type="text"
                          label="Complemento"
                          placeholder="Ex.: 3˚ andar"
                          maxLength={50}
                          value={complement || formikProps.values.complement || ''}
                          error={errors.complement || !!establishmentErrors?.complement}
                          helperText={errors.complement || establishmentErrors?.complement}
                          loading={isPostalCodeLoading}
                          onChange={e => {
                            handleChange(e);
                            formikProps.handleChange(e);
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid item sm={5} xs={12}>
                        <Input
                          name="district"
                          type="text"
                          label="Bairro"
                          placeholder="Bairro"
                          loading={isPostalCodeLoading}
                          maxLength={50}
                          value={district || formikProps.values.district || ''}
                          error={
                            isEstablishmentRegistration
                              ? (formikProps.errors.district || !!establishmentErrors?.district)
                              : (errors.district || !!establishmentErrors?.district)
                          }
                          helperText={
                            isEstablishmentRegistration
                              ? (formikProps.errors.district || establishmentErrors?.district)
                              : (errors.district || establishmentErrors?.district)
                          }
                          onChange={e => {
                            handleChange(e);
                            formikProps.handleChange(e);
                          }}
                        />
                      </Grid>
                      <Grid item sm={5} xs={6}>
                        <Input
                          name="city"
                          type="text"
                          label="Cidade"
                          placeholder="Cidade"
                          loading={isPostalCodeLoading}
                          maxLength={50}
                          value={city || formikProps.values.city || ''}
                          error={
                            isEstablishmentRegistration
                              ? (formikProps.errors.city || !!establishmentErrors?.city)
                              : (errors.city || !!establishmentErrors?.city)
                          }
                          helperText={
                            isEstablishmentRegistration
                              ? (formikProps.errors.city || establishmentErrors?.city)
                              : (errors.city || establishmentErrors?.city)
                          }
                          onChange={e => {
                            handleChange(e);
                            formikProps.handleChange(e);
                          }}
                        />
                      </Grid>
                      <Grid item sm={2} xs={6}>
                        <Input
                          name="state"
                          type="text"
                          label="UF"
                          placeholder="UF"
                          loading={isPostalCodeLoading}
                          maxLength={2}
                          value={state || formikProps.values.state || ''}
                          error={
                            isEstablishmentRegistration
                              ? (formikProps.errors.state || !!establishmentErrors?.state)
                              : (errors.state || !!establishmentErrors?.state)
                          }
                          helperText={
                            isEstablishmentRegistration
                              ? (formikProps.errors.state || establishmentErrors?.state)
                              : (errors.state || establishmentErrors?.state)
                          }
                          onChange={e => {
                            setFieldValue('state', e.target.value.toUpperCase());
                            formikProps.setFieldValue('state', e.target.value.toUpperCase());
                          }}
                        />
                      </Grid>
                    </Grid>
                    {!isStoreOnly && (
                      <>
                        {!configurationPath && (
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Input
                                name="vacanciesMarks"
                                type="number"
                                label="Total de ocupação do pátio"
                                placeholder="0"
                                maxLength={6}
                                value={vacanciesMarks || formikProps.values.vacanciesMarks}
                                error={
                                  isEstablishmentRegistration
                                    ? (formikProps.errors.vacanciesMarks || !!establishmentErrors?.vacanciesMarks)
                                    : (errors.vacanciesMarks || !!establishmentErrors?.vacanciesMarks)
                                }
                                helperText={
                                  isEstablishmentRegistration
                                    ? (formikProps.errors.vacanciesMarks || establishmentErrors?.vacanciesMarks)
                                    : (errors.vacanciesMarks || establishmentErrors?.vacanciesMarks)
                                }
                                onChange={e => {
                                  handleChange(e);
                                  formikProps.handleChange(e);
                                }}
                              />
                            </Grid>
                            {(!!userId && isUserAdmin && isEstablishmentEdit) && (
                              <Grid item xs={6}>
                                <Input
                                  disabled
                                  name="userId"
                                  type="number"
                                  label="Estabelecimento criado por"
                                  value={establishmentCreatedByUserId}
                                />
                              </Grid>
                            )}
                            {(isUserAdmin && isEstablishmentEdit) && (
                              <Grid item xs={6}>
                                <Input
                                  disabled
                                  name="businessId"
                                  type="text"
                                  label="Negócio/Business"
                                  value={businessId}
                                />
                              </Grid>
                            )}
                          </Grid>
                        )}
                      </>
                    )}
                    {(isUserAdmin && isNewEstablishment) && (
                      <Grid item xs={12}>
                        <Autocomplete
                          id="businessEstablishmentId"
                          getOptionLabel={option => getOptionLabel(option)}
                          filterOptions={filterOptions}
                          value={formattedEstablishments?.find(establishment => +establishment?.establishmentId == +businessEstablishmentId) || {}}
                          options={formattedEstablishments}
                          onChange={(_, option) => {
                            formikProps.setFieldValue('businessEstablishmentId', option?.establishmentId);
                            formikNewProps.setFieldValue('businessEstablishmentId', option?.establishmentId);

                            dispatch(loadBusinessConceptList(userId, option?.uuid));
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label="Estabelecimento Referência do Negócio"
                            />
                          )}
                        />
                      </Grid>
                    )}
                  </Loader>
                  {registerPath || (!businessPath && !configurationPath) ? (
                    <div style={{ display: 'flex', flexDirection: isSm ? 'column' : 'row', flexFlow: isSm ? 'no-wrap' : 'wrap', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                      {+isAdminUser == 1 && establishmentId ? (
                        <>
                          {(isUserAdmin && isEstablishmentEdit) && (
                            <>
                              {(+premiumPackage == 0) && (
                                <Button
                                  type="button"
                                  color="success"
                                  loading={isLoading}
                                  onClick={() => dispatch(setActivePremiumPackage(userId, establishmentId))}
                                >
                                  Ativar Pacote Premium
                                </Button>
                              )}
                              {(+premiumPackage == 1) && (
                                <Button
                                  type="button"
                                  color="error"
                                  loading={isLoading}
                                  onClick={() => dispatch(setDisablePremiumPackage(userId, establishmentId))}
                                >
                                  Desativar Pacote Premium
                                </Button>
                              )}
                            </>
                          )}
                          {(isUserAdmin && isEstablishmentEdit) && (
                            <Button
                              type="button"
                              color={debitWarning === 1 ? '#FF6600' : 'success'}
                              loading={isLoading}
                              onClick={handleEstablishmentDebit}
                              value={debitWarning}
                            >
                              Lembrete de débito
                            </Button>
                          )}
                          {(isUserAdmin && isEstablishmentEdit) && (
                            <Button
                              type="button"
                              color={appPermission == 1 ? 'error' : 'success'}
                              loading={isLoading}
                              value={appPermission}
                              onClick={handleEstablishmentPermission}
                            >
                              {appPermission == 1 ? 'Desativar estabelecimento' : 'Ativar estabelecimento'}
                            </Button>
                          )}
                        </>
                      ) : null}
                      {(!userRestrictions?.includes(RESTRICTION_PAGE.CREATE_ESTABLISHMENT) || !userRestrictions?.includes(RESTRICTION_PAGE.EDIT_ESTABLISHMENT)) && (
                        <>
                          {(registerPath && !resaleId) && (
                            <ButtonConfirmation
                              title="Adicionar"
                              color="primary"
                              disabled={isLoading}
                              loading={isLoading}
                              cancelFullWidth
                              cancelColor="tertiary"
                              cancelVariant="outlined"
                              closeOnConfirm={!superlogicaErrorsState.isError || !!superlogicaState.superlogicaId}
                              modalTitle="Selecione seu cliente de cobrança"
                              modalText={
                                <Grid container>
                                  <Grid item xs={12} style={{ backgroundColor: 'rgba(255, 152, 0, 0.9)', padding: 5, borderRadius: 8 }}>
                                    <Typography variant="subtitle1" style={{ color: '#FFFFFF' }}>
                                      Ao adicionar um novo estabelecimento estará sujeito a cobrança adicional, para mais informações acesse o <a href="https://jumppark.com.br/planos-e-precos/" target="_blank">link</a>.
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} style={{ marginTop: 10, marginBottom: 10 }}>
                                    <Select
                                      name="superlogicaId"
                                      label="Selecione um cliente"
                                      options={[
                                        ...superlogicaList,
                                        { value: 'newClientSl', label: 'Novo cliente de cobrança' }
                                      ]}
                                      value={superlogicaState.superlogicaId}
                                      onChange={event => setSuperlogicaState({ ...superlogicaState, superlogicaId: event.target.value })}
                                    />
                                    {(superlogicaState.superlogicaId == 'newClientSl') && (
                                      <Typography variant="subtitle1" color="textSecondary">
                                        Caso queira criar um novo cliente de cobrança preencha as informações abaixo:
                                      </Typography>
                                    )}
                                  </Grid>
                                  {(superlogicaState.superlogicaId == 'newClientSl') && (
                                    <>
                                      <Grid item xs={12}>
                                        <Input
                                          name="superlogicaName"
                                          type="text"
                                          label="Nome"
                                          value={superlogicaState.name}
                                          error={superlogicaErrorsState.name}
                                          helperText={superlogicaErrorsState.name}
                                          onChange={event => setSuperlogicaState({ ...superlogicaState, name: event.target.value })}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Input
                                          name="superlogicaPhone"
                                          type="phone"
                                          label="Telefone"
                                          value={superlogicaState.phone}
                                          error={superlogicaErrorsState.phone}
                                          helperText={superlogicaErrorsState.phone}
                                          onChange={event => setSuperlogicaState({ ...superlogicaState, phone: event.target.value })}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Input
                                          name="superlogicaEmail"
                                          type="email"
                                          label="Email"
                                          value={superlogicaState.email}
                                          error={superlogicaErrorsState.email}
                                          helperText={superlogicaErrorsState.email}
                                          onChange={event => setSuperlogicaState({ ...superlogicaState, email: event.target.value })}
                                        />
                                      </Grid>
                                    </>
                                  )}
                                </Grid>
                              }
                              modalButtons={[
                                {
                                  fullWidth: true,
                                  color: "success",
                                  title: "Confirmar",
                                  onClick: () => {
                                    if(!resaleId) {
                                      if(!superlogicaState.superlogicaId) {
                                        return superlogicaValidations.validate(superlogicaState)
                                          .then(() => {
                                            setSuperlogicaErrorsState({ isError: false, name: '', phone: '', email: '' });
                                            formikNewProps.handleSubmit();
                                          })
                                          .catch(error => {
                                            setSuperlogicaErrorsState({
                                              isError: true,
                                              name: '',
                                              phone: '',
                                              email: '',
                                              [error.path]: error.message
                                            });
                                          });
                                      }
                                    }

                                    return formikNewProps.handleSubmit();
                                  }
                                }
                              ]}
                            />
                          )}
                          {(registerPath && !!resaleId) && (
                            <Button color="primary" loading={isLoading}>
                              Adicionar
                            </Button>
                          )}
                          {!registerPath && (
                            <Button color="success" loading={isLoading}>
                              Salvar
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  ) : null}
                </Form>
              </Grid>
            );
          }}
        </Formik>
      </Card>
    </>
  );
}

BusinessInfo.defaultProps = {
  formikProps: {
    handleChange: () => {},
    setFieldValue: () => {},
    values: {
      establishmentName: '',
      companyName: '',
      documentId: '',
      phone: '',
      email: '',
      postalCode: '',
      street: '',
      number: 0,
      complement: '',
      district: '',
      city: '',
      state: '',
      vacanciesMarks: ''
    },
    errors: {
      companyName: false,
      documentId: false,
      phone: false,
      email: false,
      postalCode: false,
      street: false,
      number: false,
      complement: false,
      district: false,
      city: false,
      state: false,
      vacanciesMarks: false
    }
  }
}

BusinessInfo.propTypes = {
  formikProps: PropTypes.shape()
}